import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  BarChart3, 
  FileText, 
  Settings, 
  Activity, 
  Database,
  Upload,
  Download,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileUp,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import OutboundEDI from './OutboundEDI';
import EDIConfigurations from './EDIConfigurations';
import DataMappings from './DataMappings';
import EDIMonitoring from './EDIMonitoring';
import EDITransactions from './EDITransactions';
import { API_BASE_URL } from '../../config/api';

const EDI = () => {
  const { user, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch EDI dashboard data
  const { data: ediData, isLoading, error, refetch } = useQuery({
    queryKey: ['edi-dashboard'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/edi`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    enabled: hasRole(['ADMIN', 'STAFF'])
  });

  // EDI 844 file upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post(`${API_BASE_URL}/api/edi/process-844`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      return response.data;
    },
    onSuccess: (data) => {
      console.log('EDI 844 upload successful:', data);
      setUploadFile(null);
      setUploadDescription('');
      setUploadProgress(0);
      queryClient.invalidateQueries({ queryKey: ['edi-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
    onError: (error) => {
      console.error('EDI 844 upload failed:', error);
      setUploadProgress(0);
    }
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = () => {
    if (!uploadFile) return;

    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadDescription) {
      formData.append('description', uploadDescription);
    }

    uploadMutation.mutate(formData);
  };

  const handleReset = () => {
    setUploadFile(null);
    setUploadDescription('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const ediSections = [
    {
      id: 'overview',
      name: 'EDI Overview',
      icon: BarChart3,
      description: 'EDI system status and statistics'
    },
    {
      id: 'edi844',
      name: 'EDI 844 Processing',
      icon: FileUp,
      description: 'Process inbound chargeback request files'
    },
    {
      id: 'outbound',
      name: 'Outbound EDI (849 / 845)',
      icon: FileText,
      description: 'Generate & download X12 849 responses and 845 price authorizations'
    },
    {
      id: 'configurations',
      name: 'EDI Configurations',
      icon: Settings,
      description: 'EDI partner configurations and mappings'
    },
    {
      id: 'monitoring',
      name: 'EDI Monitoring',
      icon: Activity,
      description: 'Real-time EDI transaction monitoring'
    },
    {
      id: 'transactions',
      name: 'EDI Transactions',
      icon: FileText,
      description: 'View and manage EDI transactions'
    },
    {
      id: 'mappings',
      name: 'Data Mappings',
      icon: Database,
      description: 'EDI data mapping configurations'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin h-8 w-8 text-primary-600 mr-3" />
                <span className="text-gray-600">Loading EDI data...</span>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <h2 className="text-lg font-medium text-red-800 mb-2">Error Loading EDI Data</h2>
                <p className="text-red-600 mb-4">{error.message || 'Failed to load EDI dashboard data'}</p>
                <button
                  onClick={() => refetch()}
                  title="Try Again"
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                      <FileText className="h-8 w-8 text-blue-500" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Batches</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {ediData?.data?.stats?.totalBatches || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                      <RefreshCw className="h-8 w-8 text-yellow-500" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Processing</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {ediData?.data?.stats?.processingBatches || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                      <CheckCircle className="h-8 w-8 text-green-500" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Completed</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {ediData?.data?.stats?.completedBatches || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                      <XCircle className="h-8 w-8 text-red-500" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Failed</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {ediData?.data?.stats?.failedBatches || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">EDI System Status</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">EDI Service</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Success Rate</span>
                        <span className="text-sm font-medium text-gray-900">
                          {ediData?.data?.stats?.successRate || 0}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Last Update</span>
                        <span className="text-sm text-gray-900">
                          {new Date().toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Batches</h3>
                    <div className="space-y-3">
                      {ediData?.data?.recentBatches?.slice(0, 5).map((batch, index) => (
                        <div key={batch.id} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{batch.batchNumber}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(batch.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            batch.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                            batch.status === 'VALIDATING' ? 'bg-yellow-100 text-yellow-800' :
                            batch.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {batch.status}
                          </span>
                        </div>
                      )) || (
                        <p className="text-sm text-gray-500">No recent batches</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 'edi844':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">EDI 844 File Upload</h3>
              <p className="text-sm text-gray-600 mb-6">
                Upload EDI 844 chargeback request files for processing. The system will automatically 
                parse the file, validate the data, and create chargeback claims.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select EDI 844 File
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.edi,.844"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  {uploadFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Enter a description for this upload..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {uploadMutation.isSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Upload Successful</h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p>Batch: {uploadMutation.data?.data?.batchNumber}</p>
                          <p>Claims Processed: {uploadMutation.data?.data?.processedClaims}</p>
                          <p>Errors: {uploadMutation.data?.data?.errorCount}</p>
                          <p>Total Amount: ${uploadMutation.data?.data?.totalAmount?.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {uploadMutation.isError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <XCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Upload Failed</h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>{uploadMutation.error?.response?.data?.message || uploadMutation.error?.message}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={handleUpload}
                    title="Upload & Process"
                    disabled={!uploadFile || uploadMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadMutation.isPending ? (
                      <>
                        <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload & Process
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleReset}
                    title="Reset"
                    disabled={uploadMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">EDI 844 File Format</h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p>The EDI 844 file should contain chargeback transaction data in tab-delimited format with the following fields:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Manufacturer ID</li>
                  <li>Contract Number</li>
                  <li>Customer ID</li>
                  <li>Contract Alias</li>
                  <li>Claim Number</li>
                  <li>Total Contract Amount</li>
                  <li>Claim Date (YYYYMMDD)</li>
                  <li>Invoice Number</li>
                  <li>Invoice Date (YYYYMMDD)</li>
                  <li>Reference Number</li>
                  <li>Sales Date (YYYYMMDD)</li>
                  <li>Quantity</li>
                  <li>Unit Price</li>
                  <li>Chargeback Amount</li>
                  <li>Net Amount</li>
                  <li>Customer Name</li>
                  <li>Customer Address</li>
                  <li>Customer City</li>
                  <li>Customer State</li>
                  <li>Customer ZIP</li>
                  <li>Customer Type</li>
                  <li>Unit of Measure</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'outbound':
        return <OutboundEDI />;

      case 'configurations':
        return <EDIConfigurations />;

      case 'monitoring':
        return <EDIMonitoring />;

      case 'transactions':
        return <EDITransactions />;

      case 'mappings':
        return <DataMappings />;

      default:
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600">Select a section to view details.</p>
          </div>
        );
    }
  };

  // Check if user has access to EDI
  if (!hasRole(['ADMIN', 'STAFF'])) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">You don't have permission to access the EDI management section.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">EDI Management</h1>
        <p className="text-gray-600 mt-2">
          Electronic Data Interchange (EDI) system management and monitoring
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">EDI Sections</h2>
            </div>
            <nav className="p-2">
              {ediSections.map((section) => (
                <button
                  key={section.id}
                  title={section.name}
                  onClick={() => setActiveTab(section.id)}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === section.id
                      ? 'bg-primary-100 text-primary-900 border-l-4 border-primary-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center">
                    <section.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                    <span>{section.name}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-7">{section.description}</p>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default EDI;
